import { Link } from "@tanstack/react-router";
import {
  ArrowRight,
  Building2,
  Coins,
  GraduationCap,
  HeartPulse,
  Lightbulb,
  PiggyBank,
  Sparkles,
  Users,
} from "lucide-react";
import { useTaxPlays } from "@/hooks/useTaxPlays";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/Card";
import { Badge } from "@/components/ui/Badge";
import { Skeleton } from "@/components/ui/Skeleton";
import { PageHeader } from "@/components/layout/PageHeader";
import { useCashAllocation } from "@/hooks/useStrategy";
import { useFamilyProfile } from "@/hooks/useProfile";
import { formatMoney, formatPercent } from "@/lib/utils";

interface Tile {
  to: string;
  title: string;
  body: string;
  icon: typeof Coins;
  status: string;
  badge?: "info" | "success" | "warning" | "danger";
}

export function StrategyHubPage() {
  const cash = useCashAllocation();
  const family = useFamilyProfile();
  const taxPlays = useTaxPlays();

  const plan = cash.data?.plan;
  const planReady = plan && !plan.isBlocked;

  const applicablePlays = taxPlays.data?.plays.filter((p) => p.isApplicable) ?? [];
  const quantifiedTotal = applicablePlays
    .filter((p) => p.estimatedDollarImpact != null)
    .reduce((s, p) => s + (p.estimatedDollarImpact ?? 0), 0);

  const tiles: Tile[] = [
    {
      to: "/strategy/tax-plays",
      title: "Tax Plays — concrete dollar moves",
      body: applicablePlays.length > 0
        ? `${applicablePlays.length} plays applicable today${quantifiedTotal > 0 ? `, ~${formatMoney(quantifiedTotal)} in year-1 tax saved if you take them all` : ""}.`
        : "Vehicle conversion (Section 179), home office, cell/internet, Augusta Rule, EOY equipment bunching, hire-kids, HSA, Solo 401(k) profit-share — each with mechanic + IRS reference.",
      icon: Lightbulb,
      status: "Browse",
      badge: "info",
    },
    {
      to: "/strategy/entity",
      title: "Entity Transition Center",
      body: "Side-by-side total household tax across SMLLC / S-Corp / S-Corp + FMLLC / C-Corp using your real numbers.",
      icon: Building2,
      status: "Compare",
      badge: "info",
    },
    {
      to: "/strategy/cash",
      title: "Cash Allocation",
      body: planReady
        ? `${formatPercent(plan.taxSavingsSweepPercent, 0)} tax · ${formatPercent(plan.retirementHoldingPercent, 0)} retirement · ${formatPercent(plan.ownerDrawPercent, 0)} owner draw`
        : "Add your rate, hours, and tax intake — then we split each dollar between tax / retirement / buffer / draw using the engine's marginal rate.",
      icon: Coins,
      status: planReady ? "Auto-computed" : "Needs intake",
      badge: planReady ? "success" : "warning",
    },
    {
      to: "/strategy/retirement",
      title: "Solo 401(k) + HSA + 529",
      body: planReady
        ? `Target ${formatMoney(plan.projectedAnnualSolo401k)} into Solo 401(k); HSA + Edvest 529 also tracked here.`
        : "Open the plan by 12/31. Mega Backdoor Roth requires a provider that supports after-tax + in-plan conversions.",
      icon: PiggyBank,
      status: "Open by Dec 31",
      badge: "warning",
    },
    {
      to: "/strategy/hire-family",
      title: "Hire spouse + kids",
      body: family.data
        ? `${family.data.children.length} ${family.data.children.length === 1 ? "child" : "children"} entered; ${family.data.children.filter((c) => c.isOnPayroll).length} on payroll.`
        : "Reasonable wages to legitimate work shifts income to lower brackets and unlocks Roth IRA space.",
      icon: Users,
      status: "Plan",
      badge: "info",
    },
    {
      to: "/strategy/retirement#hsa",
      title: "HSA",
      body: "Triple tax advantage. Family limit $8,550 (2025). Pair with HDHP.",
      icon: HeartPulse,
      status: "Enroll",
    },
    {
      to: "/strategy/retirement#529",
      title: "529 plans",
      body: "Wisconsin Edvest deduction up to $4,260 per beneficiary in 2025.",
      icon: GraduationCap,
      status: "Optional",
    },
  ];

  const cheatsheet = [
    { label: "Filing", value: family.data?.filingStatus ?? "—" },
    { label: "Children", value: String(family.data?.children.length ?? 0) },
    {
      label: "Kids on payroll",
      value: String(family.data?.children.filter((c) => c.isOnPayroll).length ?? 0),
    },
    { label: "HSA-eligible", value: family.data?.isHsaEligible ? "Yes" : "Not yet" },
    {
      label: "Sweep %",
      value: planReady ? formatPercent(plan.taxSavingsSweepPercent, 0) : "—",
    },
    {
      label: "Marginal next-$ rate",
      value: planReady ? formatPercent(plan.marginalTotalRate, 1) : "—",
    },
  ];

  return (
    <>
      <PageHeader
        eyebrow="Strategy Hub"
        title="Tax smart first, then growth"
        description="Each tile is a lever. Caldera re-evaluates them as your numbers and life events change."
        actions={
          <Badge tone="info" className="gap-1">
            <Sparkles className="h-3 w-3" />
            Personalized
          </Badge>
        }
      />

      <Card className="mb-6">
        <CardHeader>
          <CardTitle>Quick context</CardTitle>
          <CardDescription>Inputs the strategy hub is using right now.</CardDescription>
        </CardHeader>
        <CardContent>
          {family.isLoading || cash.isLoading ? (
            <Skeleton className="h-16 w-full" />
          ) : (
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-6">
              {cheatsheet.map((c) => (
                <div key={c.label}>
                  <div className="text-xs text-muted-foreground">{c.label}</div>
                  <div className="text-sm font-semibold">{c.value}</div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
        {tiles.map((t) => {
          const Icon = t.icon;
          return (
            <Link key={t.to} to={t.to} className="block focus-ring rounded-xl">
              <Card className="h-full transition-colors hover:border-primary/50">
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-primary">
                      <Icon className="h-4 w-4" />
                      {t.status}
                    </div>
                    {t.badge && <Badge tone={t.badge}>{t.badge}</Badge>}
                  </div>
                  <CardTitle className="mt-1 flex items-center justify-between gap-2">
                    {t.title}
                    <ArrowRight className="h-4 w-4 text-muted-foreground" />
                  </CardTitle>
                  <CardDescription>{t.body}</CardDescription>
                </CardHeader>
                <CardContent />
              </Card>
            </Link>
          );
        })}
      </div>
    </>
  );
}
